import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import accentInsensitiveSearch from 'accent-insensitive-search';
import { Municipality } from 'app/addresses/store/municipalities/municipalities.model';
import { combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, mergeMap, startWith, takeUntil, tap } from 'rxjs/operators';
import { FetchMemberPublicInfos, LoadAdministrativeRegions, LoadInsuranceStatusCodes, LoadSpecialities, SearchMunicipality, SearchPublicMemberBy } from './store/public-member-search.actions';
import { AdministrativeRegion, InsuranceStatusCode, PublicMember, PublicMemberInfos, SpecialtyType } from './store/public-member-search.model';
import { PublicMemberSearchState } from './store/public-member-search.state';

@Component({
    selector: 'odq-public-member-search',
    templateUrl: './public-member-search.component.html',
    styleUrls: ['./public-member-search.component.scss'],
    standalone: false
})
export class PublicMemberSearchComponent implements OnInit, OnDestroy {

  destroy$ = new Subject<void>();

  formGroup: UntypedFormGroup;

  filteredSpecialities$: Observable<SpecialtyType[]>;

  @Select(PublicMemberSearchState.members)
  members$: Observable<PublicMember[]>

  @Select(PublicMemberSearchState.specialities)
  specialities$: Observable<SpecialtyType[]>;
  specialities: SpecialtyType[] = [];

  @Select(PublicMemberSearchState.municipalities)
  filteredMunicipalitys$: Observable<Municipality[]>;

  @Select(PublicMemberSearchState.memberPublicInfos)
  memberPublicInfos$: Observable<PublicMemberInfos>;

  @Select(PublicMemberSearchState.memberPublicInfosLoading)
  memberPublicInfosLoading$: Observable<boolean>;

  @Select(PublicMemberSearchState.administrativeRegions)
  administrativeRegions$: Observable<AdministrativeRegion[]>;

  @Select(PublicMemberSearchState.searching)
  searching$: Observable<boolean>;

  @Select(PublicMemberSearchState.insuranceStatusCodes)
  insuranceStatusCodes$: Observable<InsuranceStatusCode[]>

  filteredAdministrativeRegions$: Observable<AdministrativeRegion[]>;

  showSearchForm = true;
  showResultList = false;
  showMemberinfoBox = false;
  fromSearch = false;
  searchIsDisabled = true;

  @ViewChild('scrollElement') private scrollContainer: ElementRef;

  selectedMunicipality: Municipality;
  selectedSpecialty: SpecialtyType;
  selectedAdministrativeRegion: AdministrativeRegion;
  isAnesthesiaHospital: boolean;
  isAnesthesiaPrivateClinic: boolean;
  isParticularMedicalCondition: boolean;
  isHomeVisit: boolean;
  isSleepApnea: boolean;
  isReducedMobility: boolean;
  isLiftPerson: boolean;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private Activatedroute: ActivatedRoute,
    public translate: TranslateService,
  ) { }

  ngOnInit(): void {

    // Init form
    this.formGroup = this.fb.group({
      member: [],
      speciality: [],
      municipality: [],
      administrativeRegion: [],
      isAnesthesiaHospital: [],
      isAnesthesiaPrivateClinic: [],
      isParticularMedicalCondition: [],
      isHomeVisit: [],
      isSleepApnea: [],
      isReducedMobility: [],
      isLiftPerson: []
    })

    // Load specialtytypes
    this.store.dispatch(new LoadSpecialities())

    this.specialities$.pipe(
      tap(res => this.specialities = res),
      takeUntil(this.destroy$),
    ).subscribe();

    this.store.dispatch(new LoadAdministrativeRegions());

    this.store.dispatch(new LoadInsuranceStatusCodes());

    // Set language
    this.Activatedroute.queryParamMap.pipe(
      tap(params => {
        const lang = params.get('lang');
        if (!!lang) {
          this.translate.use(lang);
        }
      }),
      takeUntil(this.destroy$),
    ).subscribe();

    // init fields
    this._initFieldListeners();

    this._removeUnecessaryCssClasses();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private _removeUnecessaryCssClasses() {
    /*
      This css class is causing the iframe on the client to not resize based on 
      this component content height changes 
    */
    document.body.classList.remove("vh-100");
  }

  private _initFieldListeners() {

    // filtering specialties
    this.filteredSpecialities$ = this.formGroup.get('speciality').valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      map(value => typeof value === 'string' ? value : value?.description[this.translate.currentLang]),
      map(value => this._filterSpeciality(value)),
      takeUntil(this.destroy$),
    )

    // filtering municipalities
    this.formGroup.get('municipality').valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      map(value => typeof value === 'string' ? value : value?.name),
      tap(searchTerm => this.store.dispatch(new SearchMunicipality(searchTerm))),
      takeUntil(this.destroy$),
    ).subscribe()

    // filtering administrative regions
    this.filteredAdministrativeRegions$ = this.formGroup.get('administrativeRegion').valueChanges.pipe(
      filter(value => typeof value === 'string'),
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      mergeMap(administrativeRegionSearch => this.administrativeRegions$.pipe(
        map(admRegions => admRegions.filter(admReg => accentInsensitiveSearch(administrativeRegionSearch, admReg.description)))
      ))
    );


    // search members by municipality or specialty
    const selectedMunicipality$ = this.formGroup.get('municipality').valueChanges.pipe(
      filter(value => typeof value !== 'string'),
      tap(value => this.selectedMunicipality = value),
      takeUntil(this.destroy$)
    );

    const selectedSpecialty$ = this.formGroup.get('speciality').valueChanges.pipe(
      filter(value => typeof value !== 'string'),
      tap(value => this.selectedSpecialty = value),
      takeUntil(this.destroy$)
    )

    const selectedAdministrativeRegions$ = this.formGroup.get('administrativeRegion').valueChanges.pipe(
      filter(value => typeof value !== 'string'),
      tap(value => this.selectedAdministrativeRegion = value),
      takeUntil(this.destroy$)
    )

    const isAnesthesiaHospital$ = this.formGroup.get('isAnesthesiaHospital').valueChanges.pipe(
      filter(value => typeof value !== 'string'),
      tap(value => {
        this.isAnesthesiaHospital = value;
      }),
      takeUntil(this.destroy$)
    )

    const isAnesthesiaPrivateClinic$ = this.formGroup.get('isAnesthesiaPrivateClinic').valueChanges.pipe(
      filter(value => typeof value !== 'string'),
      tap(value => {
        this.isAnesthesiaPrivateClinic = value;
      }),
      takeUntil(this.destroy$)
    )

    const isParticularMedicalCondition$ = this.formGroup.get('isParticularMedicalCondition').valueChanges.pipe(
      filter(value => typeof value !== 'string'),
      tap(value => {
        this.isParticularMedicalCondition = value
      }),
      takeUntil(this.destroy$)
    )

    const isHomeVisit$ = this.formGroup.get('isHomeVisit').valueChanges.pipe(
      filter(value => typeof value !== 'string'),
      tap(value => {
        this.isHomeVisit = value
      }),
      takeUntil(this.destroy$)
    )

    const isSleepApnea$ = this.formGroup.get('isSleepApnea').valueChanges.pipe(
      filter(value => typeof value !== 'string'),
      tap(value => {
        this.isSleepApnea = value;
      }),
      takeUntil(this.destroy$)
    )

    const isReducedMobility$ = this.formGroup.get('isReducedMobility').valueChanges.pipe(
      filter(value => typeof value !== 'string'),
      tap(value => {
        this.isReducedMobility = value;
      }),
      takeUntil(this.destroy$)
    )

    const isLiftPerson$ = this.formGroup.get('isLiftPerson').valueChanges.pipe(
      filter(value => typeof value !== 'string'),
      tap(value => {
        this.isLiftPerson = value;
      }),
      takeUntil(this.destroy$)
    )

    combineLatest([
      selectedMunicipality$.pipe(startWith('')),
      selectedSpecialty$.pipe(startWith('')),
      selectedAdministrativeRegions$.pipe(startWith('')),
      isAnesthesiaHospital$.pipe(startWith(false)),
      isParticularMedicalCondition$.pipe(startWith(false)),
      isHomeVisit$.pipe(startWith(false)),
      isSleepApnea$.pipe(startWith(false)),
      isReducedMobility$.pipe(startWith(false)),
      isAnesthesiaPrivateClinic$.pipe(startWith(false)),
      isLiftPerson$.pipe(startWith(false)),
    ]).pipe(
      tap(res => {
        if (!!res[0] || !!res[1] || !!res[2] || !!res[3] || !!res[4] || !!res[5] || !!res[6] || !!res[7] || !!res[8] || res[9]) {
          this.searchIsDisabled = false;
        } else {
          this.searchIsDisabled = true;
        }
      })
    ).subscribe();

    this.formGroup.get('member').valueChanges.pipe(
      filter(member => !!member?.id),
      tap(member => {
        if (!!member.id) {
          this._fetchMemberInfos(member.id)
        }
      })
    ).subscribe()
  }

  clearInput(inputName: string) {
    this.formGroup.get(inputName).setValue(null);
  }

  displaySpecialtyBinded = this.displaySpecialty.bind(this);

  displaySpecialty(specialty: SpecialtyType): string {
    return specialty?.description[this.translate.currentLang] || '';
  }

  displayMunicipality(municipality: Municipality): string {
    return !!municipality ? `${municipality.name} - ${municipality.provinceName}` : '';
  }

  displayAdministrativeRegion(administrativeRegion: AdministrativeRegion): string {
    return administrativeRegion?.description || '';
  }

  displayIsAnesthesiaHospital() {
    if (this.isAnesthesiaHospital) {
      return this.translate.instant('publicMemberSearch.isAnesthesiaHospital')
    }
  }

  displayIsAnesthesiaPrivateClinic() {
    if (this.isAnesthesiaPrivateClinic) {
      return this.translate.instant('publicMemberSearch.isAnesthesiaPrivateClinic')
    }
  }

  displayIsParticularMedicalCondition() {
    if (this.isParticularMedicalCondition) {
      return this.translate.instant('publicMemberSearch.isParticularMedicalCondition')
    }
  }

  displayIsHomeVisit() {
    if (this.isHomeVisit) {
      return this.translate.instant('publicMemberSearch.isHomeVisit')
    }
  }

  displayIsSleepApnea() {
    if (this.isSleepApnea) {
      return this.translate.instant('publicMemberSearch.isSleepApnea')
    }
  }

  displayIsReducedMobility() {
    if (this.isReducedMobility) {
      return this.translate.instant('publicMemberSearch.isReducedMobility')
    }
  }

  displayIsLiftPerson() {
    if (this.isLiftPerson) {
      return this.translate.instant('publicMemberSearch.isLiftPerson')
    }
  }

  private _filterSpeciality(value: string): SpecialtyType[] {
    return this.specialities?.filter(option => accentInsensitiveSearch(value, option.description[this.translate.currentLang]));
  }

  selectMember($event, memberId) {
    this.fromSearch = true;
    $event.preventDefault();
    this._fetchMemberInfos(memberId)
  }

  private _fetchMemberInfos(memberId: string) {
    this.showMemberinfoBox = true;
    this.showResultList = false;
    this.showSearchForm = false;
    this.store.dispatch(new FetchMemberPublicInfos(memberId))
    // Scroll To top
    setTimeout(() => {
      this.scrollContainer.nativeElement.scrollTop = 0;
    }, 100);
  }

  newSearch() {
    this.showMemberinfoBox = false;
    this.showResultList = false;
    this.showSearchForm = true;
    this.fromSearch = false;
    this.formGroup.reset();
  }

  search() {
    let
      {
        speciality,
        municipality,
        administrativeRegion,
        isAnesthesiaHospital,
        isAnesthesiaPrivateClinic,
        isParticularMedicalCondition,
        isHomeVisit,
        isSleepApnea,
        isReducedMobility,
        isLiftPerson } = this.formGroup.value;

    this.store.dispatch(new SearchPublicMemberBy(municipality?.name, speciality?.id, administrativeRegion?.code, isAnesthesiaHospital, isAnesthesiaPrivateClinic, isParticularMedicalCondition, isHomeVisit, isSleepApnea, isReducedMobility, isLiftPerson));

    this.showMemberinfoBox = false;
    this.showResultList = true;
    this.showSearchForm = false;
    this.fromSearch = false;
  }

}
